// @services/firebaseChat.js
import { doc, setDoc, getDocs, collection, onSnapshot, getDoc } from 'firebase/firestore';
import { getFirebase, getCurrentUserEnv } from './firebase';
import { readGlobalProfiles } from '@services/firebaseGlobal';
export { markChatAsRead as markMessagesAsRead };

// ==================== Helper Functions (no db needed) ====================

export const getUserRoleForChat = async (userId) => {
  try {
    if (!userId) {
      console.warn('No user ID provided to getUserRoleForChat');
      return 'User';
    }

    const userList = await readGlobalProfiles('cUsers');
    if (!userList || !Array.isArray(userList)) {
      console.error('User list is not available');
      return 'User';
    }

    const userData = userList.find(user => user && user.id === userId);

    if (userData) {
      if (userData.isadminuser) return 'Admin';
      if (userData.issuperuser) return 'Super';
      if (userData.ismover) return 'Mover';
      if (userData.isbuyer) return 'Buyer';
    }
    return 'User';
  } catch (error) {
    console.error('Error getting user role:', error);
    return 'User';
  }
};

export const findSuperUserId = async () => {
  try {
    const currentEnv = getCurrentUserEnv();
    const userList = await readGlobalProfiles('cUsers');
    if (!userList || !Array.isArray(userList)) {
      console.error('User list is not available for finding Super user');
      return null;
    }

    const superUser = userList.find(user => user && user.issuperuser && (user.env || 'Prod') === currentEnv);
    return superUser?.id || null;
  } catch (error) {
    console.error('Error finding Super user:', error);
    return null;
  }
};

// ==================== Core Chat Functions (require db) ====================

const getTargetChatId = async (senderId, receiverId = null) => {
  try {
    if (!senderId) {
      throw new Error('No sender ID provided to getTargetChatId');
    }

    const senderRole = await getUserRoleForChat(senderId);
    const receiverRole = receiverId ? await getUserRoleForChat(receiverId) : null;

    // Super user sending message
    if (senderRole === 'Super') {
      // Super to Admin - store in Super's chat (Admin doesn't have own chat)
      if (receiverRole === 'Admin') {
        return senderId; // Store in Super's profile
      }
      // Super to regular user (Mover, Buyer, User) - store in USER'S profile
      if (receiverRole === 'Mover' || receiverRole === 'Buyer' || receiverRole === 'User') {
        return receiverId; // Store in user's profile
      }
      // Super to another Super - store in sender's profile
      return senderId;
    }

    // Regular user (Mover, Buyer, User) sending to Super - store in Super's chat
    if ((senderRole === 'Mover' || senderRole === 'Buyer' || senderRole === 'User') && receiverRole === 'Super') {
      const superUserId = await findSuperUserId();
      if (!superUserId) throw new Error('No Super user found');
      return superUserId; // Store in Super's profile
    }

    // Admin user sending to Super - store in Super's chat
    if (senderRole === 'Admin' && receiverRole === 'Super') {
      const superUserId = await findSuperUserId();
      if (!superUserId) throw new Error('No Super user found');
      return superUserId; // Store in Super's profile
    }

    // Admin user sending to regular user - store in user's profile
    if (senderRole === 'Admin' && (receiverRole === 'Mover' || receiverRole === 'Buyer' || receiverRole === 'User')) {
      return receiverId;
    }

    // Regular user to regular user - store in sender's profile
    return senderId;
  } catch (error) {
    console.error('Error getting target chat ID:', error);
    throw error;
  }
};

export const readUserChat = async (userId) => {
  const { db } = getFirebase();
  try {
    if (!userId) {
      console.error('No user ID provided to readUserChat');
      return null;
    }

    if (typeof userId !== 'string' || userId.trim() === '') {
      console.error('Invalid user ID provided to readUserChat:', userId);
      return null;
    }

    const chatDoc = doc(db, 'cChats', userId);
    const chatSnapshot = await getDoc(chatDoc);

    if (chatSnapshot.exists()) {
      return {
        id: chatSnapshot.id,
        ...chatSnapshot.data()
      };
    }
    return null;
  } catch (error) {
    console.error('Error reading user chat for ID:', userId, error);
    return null;
  }
};

export const readAllChats = async (currentUserId) => {
  const { db } = getFirebase();
  try {
    if (!currentUserId) {
      console.error('No current user ID provided to readAllChats');
      return [];
    }

    const currentUserRole = await getUserRoleForChat(currentUserId);
    const allChatsSnapshot = await getDocs(collection(db, 'cChats'));
    const allChats = allChatsSnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));

    // Admin user: Read from Super's chat data AND all regular user chats
    if (currentUserRole === 'Admin') {
      const superUserId = await findSuperUserId();
      const chats = [];

      // Add Super's chat (contains Admin-Super conversations)
      if (superUserId) {
        const superChat = allChats.find(chat => chat.id === superUserId);
        if (superChat) {
          chats.push(superChat);
        }
      }

      // Add all regular user chats (Mover, Buyer, User) - EXCLUDE Admin chats
      const regularUserChats = allChats.filter(chat => {
        const userRole = chat.userRole;
        return (userRole === 'Mover' || userRole === 'Buyer' || userRole === 'User') && userRole !== 'Admin';
      });

      return [...chats, ...regularUserChats];
    }

    // Super user: Read their own chat + all user chats where they have conversations
    if (currentUserRole === 'Super') {
      const superChat = allChats.find(chat => (chat.id === currentUserId));
      const resultChats = [];

      // Add Super's own chat
      if (superChat) {
        resultChats.push(superChat);
      }

      // Find ALL chats where Super is involved (except their own)
      const chatsWithSuper = allChats.filter(chat => {
        if (chat.id === currentUserId) return false;

        // Check if this chat has any messages involving Super
        const hasSuperMessages = chat.messages?.some(msg =>
          msg.senderId === currentUserId || msg.receiverId === currentUserId || chat.chatWith === 'support'
        );

        return hasSuperMessages;
      });
      return [...resultChats, ...chatsWithSuper];
    }

    // Regular users: Only read their own chat
    const userChat = allChats.find(chat => chat.id === currentUserId);
    return userChat ? [userChat] : [];

  } catch (error) {
    console.error('Error reading all chats:', error);
    return [];
  }
};

export const writeUserChat = async (userId, chatData) => {
  const { db } = getFirebase();
  try {
    if (!userId) {
      throw new Error('No user ID provided for writeUserChat');
    }

    if (typeof userId !== 'string' || userId.trim() === '') {
      throw new Error('Invalid user ID provided for writeUserChat: ' + userId);
    }

    // CHECK: Never create Admin documents
    const userRole = await getUserRoleForChat(userId);
    if (userRole === 'Admin') {
      console.error('🚫 ATTEMPT TO CREATE ADMIN CHAT DOCUMENT BLOCKED:', userId);
      throw new Error('Admin chat documents should never be created. Store in Super profile instead.');
    }
    await setDoc(doc(db, 'cChats', userId), chatData);
  } catch (error) {
    console.error('Error writing user chat:', error);
    throw error;
  }
};

export const addChatMessage = async (senderId, messageData) => {
  const { db } = getFirebase(); // (not directly used here but kept for consistency)
  try {
    if (!senderId) {
      throw new Error('No sender ID provided for addChatMessage');
    }

    if (!messageData || !messageData.text) {
      throw new Error('Invalid message data: text is required');
    }

    const targetChatId = await getTargetChatId(senderId, messageData.receiverId);

    if (!targetChatId) {
      throw new Error('Could not determine target chat ID');
    }

    const currentChat = await readUserChat(targetChatId);
    const messages = currentChat?.messages || [];
    const existingMessages = currentChat?.messages || [];

    const updatedMessages = existingMessages.map(msg => ({
      ...msg,
      lastMessage: false
    }));

    const newMessage = {
      id: `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      text: String(messageData.text || '').trim(),
      senderId: String(senderId || ''),
      senderRole: String(messageData.senderRole || 'User'),
      receiverId: messageData.receiverId ? String(messageData.receiverId) : null,
      receiverRole: messageData.receiverRole ? String(messageData.receiverRole) : null,
      type: String(messageData.type || 'text'),
      timestamp: new Date().toISOString(),
      lastMessage: true,
      read: false,
      fileData: messageData.fileData || null
    };

    // If this is a support response and there are existing messages without receiverId,
    // update the first message with the receiverId
    if (messageData.receiverId && existingMessages.length > 0) {
      const firstMessage = existingMessages[0];
      if (!firstMessage.receiverId) {
        updatedMessages[0] = {
          ...firstMessage,
          receiverId: String(messageData.senderId || ''),
          receiverRole: String(messageData.senderRole || 'User')
        };
      }
    }

    const targetUserRole = await getUserRoleForChat(targetChatId);
    const bookmarked = true;

    const updatedChat = {
      id: String(targetChatId || ''),
      userRole: String(currentChat?.userRole || targetUserRole || 'User'),
      chatWith: currentChat?.chatWith || (targetUserRole !== 'Admin' && targetUserRole !== 'Super') ? 'support' : null,
      updatedAt: new Date().toISOString(),
      messages: [...updatedMessages, newMessage],
      unreadCount: Number(currentChat?.unreadCount || 0) + 1,
      bookmarked: bookmarked,
      createdAt: currentChat?.createdAt || new Date().toISOString()
    };
    await writeUserChat(targetChatId, updatedChat);

    return newMessage;
  } catch (error) {
    console.error('❌ Error adding chat message:', error);
    console.error('❌ Error details:', {
      senderId,
      messageData,
      stack: error.stack
    });
    throw error;
  }
};

export const initializeUserChat = async (userId, userRole) => {
  const { db } = getFirebase();
  try {
    if (!userId) {
      throw new Error('No user ID provided for initializeUserChat');
    }

    if (typeof userId !== 'string' || userId.trim() === '') {
      throw new Error('Invalid user ID provided for initializeUserChat: ' + userId);
    }

    const actualUserRole = userRole || await getUserRoleForChat(userId);
    if (actualUserRole === 'Admin') {
      console.error('🚫 ATTEMPT TO INITIALIZE ADMIN-OWNED CHAT BLOCKED:', userId);
      throw new Error('Admin-owned chats should not be initialized. Use Super profile for Admin conversations.');
    }

    const existingChat = await readUserChat(userId);

    if (!existingChat) {
      const chatData = {
        id: userId,
        userRole: actualUserRole,
        chatWith: (actualUserRole !== 'Admin' && actualUserRole !== 'Super') ? 'support' : null,
        unreadCount: 0,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
        messages: [],
        bookmarked: true
      };

      await writeUserChat(userId, chatData);
      return chatData;
    }

    if (existingChat.bookmarked === undefined) {
      const updatedChat = {
        ...existingChat,
        bookmarked: true,
        updatedAt: new Date().toISOString()
      };
      await writeUserChat(userId, updatedChat);
      return updatedChat;
    }
    return existingChat;
  } catch (error) {
    console.error('Error initializing user chat:', error);
    throw error;
  }
};

// ==================== Real-time Subscriptions ====================

export const subscribeToUserChat = (userId, callback) => {
  const { db } = getFirebase();
  try {
    if (!userId) {
      console.error('No user ID provided for subscribeToUserChat');
      return () => { };
    }

    if (typeof userId !== 'string' || userId.trim() === '') {
      console.error('Invalid user ID provided for subscribeToUserChat:', userId);
      return () => { };
    }

    const unsubscribe = onSnapshot(doc(db, 'cChats', userId), (doc) => {
      if (doc.exists()) {
        callback({ id: doc.id, ...doc.data() });
      } else {
        callback(null);
      }
    }, (error) => {
      console.error('Error in real-time listener for user:', userId, error);
    });

    return unsubscribe;
  } catch (error) {
    console.error('Error setting up real-time listener:', error);
    return () => { };
  }
};

export const subscribeToAllChats = (currentUserId, callback) => {
  const { db } = getFirebase();
  try {
    if (!currentUserId) {
      console.error('No current user ID provided for subscribeToAllChats');
      return () => { };
    }

    return onSnapshot(collection(db, 'cChats'), async (snapshot) => {
      const allChats = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));

      const currentUserRole = await getUserRoleForChat(currentUserId);

      if (currentUserRole === 'Admin') {
        const superUserId = await findSuperUserId();
        const filteredChats = [];

        if (superUserId) {
          const superChat = allChats.find(chat => chat.id === superUserId);
          if (superChat) {
            filteredChats.push(superChat);
          }
        }

        const regularChats = allChats.filter(chat => {
          const userRole = chat.userRole;
          return userRole === 'Mover' || userRole === 'Buyer' || userRole === 'User';
        });

        callback([...filteredChats, ...regularChats]);

      } else if (currentUserRole === 'Super') {
        const superChat = allChats.find(chat => chat.id === currentUserId);
        const resultChats = [];

        if (superChat) {
          resultChats.push(superChat);
        }

        const chatsWithSuper = allChats.filter(chat => {
          if (chat.id === currentUserId) return false;
          return chat.chatWith === 'support' || chat.messages?.some(msg =>
            msg.senderId === currentUserId || msg.receiverId === currentUserId
          );
        });

        callback([...resultChats, ...chatsWithSuper]);

      } else {
        const userChat = allChats.find(chat => chat.id === currentUserId);
        callback(userChat ? [userChat] : []);
      }
    }, (error) => {
      console.error('Error in real-time listener for all chats:', error);
    });

  } catch (error) {
    console.error('Error setting up real-time listener for all chats:', error);
    return () => { };
  }
};

export const markChatAsRead = async (openedChatId, currentUserId) => {
  const { db } = getFirebase();
  try {
    const currentUserRole = await getUserRoleForChat(currentUserId);
    const chatUserRole = await getUserRoleForChat(openedChatId);

    let targetChatId = openedChatId;

    if (currentUserRole === 'Super' && chatUserRole === 'Admin') {
      targetChatId = currentUserId;
    } else if (currentUserRole === 'Admin' && chatUserRole === 'Super') {
      targetChatId = openedChatId;
    } else if ((currentUserRole === 'Admin' || currentUserRole === 'Super') && (chatUserRole !== 'Super' && chatUserRole !== 'Admin')) {
      targetChatId = openedChatId;
    }

    const targetChat = await readUserChat(targetChatId);

    if (!targetChat?.messages || !Array.isArray(targetChat.messages)) {
      return;
    }

    let readCount = 0;
    const updatedMessages = targetChat.messages.map(msg => {
      if (currentUserRole === 'Super' && chatUserRole === 'Admin') {
        if (msg.senderRole === 'Admin' && msg.receiverId === currentUserId && !msg.read) {
          readCount++;
          return {
            ...msg,
            read: true,
            readAt: new Date().toISOString()
          };
        }
      } else if (currentUserRole === 'Admin' && chatUserRole === 'Super') {
        if (msg.senderId === openedChatId && msg.receiverId === currentUserId && !msg.read) {
          readCount++;
          return {
            ...msg,
            read: true,
            readAt: new Date().toISOString()
          };
        }
      } else if ((currentUserRole === 'Super' || currentUserRole === 'Admin') && (chatUserRole !== 'Super' && chatUserRole !== 'Admin')) {
        if (msg.senderId === openedChatId && !msg.read) {
          readCount++;
          return {
            ...msg,
            read: true,
            readAt: new Date().toISOString()
          };
        }
      } else if (msg.receiverId === currentUserId && !msg.read) {
        readCount++;
        return {
          ...msg,
          read: true,
          readAt: new Date().toISOString()
        };
      }

      return msg;
    });

    if (readCount === 0) {
      return targetChat;
    }

    const updatedChat = {
      ...targetChat,
      messages: updatedMessages,
      unreadCount: Math.max(0, (targetChat.unreadCount || 0) - readCount),
      lastReadAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
      bookmarked: targetChat.bookmarked !== undefined ? targetChat.bookmarked : true
    };

    await writeUserChat(targetChatId, updatedChat);

    return updatedChat;
  } catch (error) {
    console.error('❌ Error marking chat as read:', error);
    throw error;
  }
};

export const updateMessage = async (targetChatId, messageId, updatedData) => {
  const { db } = getFirebase();
  try {
    if (!targetChatId || !messageId) {
      throw new Error('Target chat ID and message ID are required');
    }

    const chat = await readUserChat(targetChatId);
    if (!chat) {
      throw new Error('Chat not found');
    }

    const updatedMessages = chat.messages.map(msg => {
      if (msg.id === messageId) {
        return {
          ...msg,
          ...updatedData,
          editedAt: new Date().toISOString(),
          isEdited: true
        };
      }
      return msg;
    });

    const updatedChat = {
      ...chat,
      messages: updatedMessages,
      updatedAt: new Date().toISOString()
    };

    await writeUserChat(targetChatId, updatedChat);
    return updatedChat;
  } catch (error) {
    console.error('Error updating message:', error);
    throw error;
  }
};

export const deleteMessage = async (targetChatId, messageId) => {
  const { db } = getFirebase();
  try {
    if (!targetChatId || !messageId) {
      throw new Error('Target chat ID and message ID are required');
    }

    const chat = await readUserChat(targetChatId);
    if (!chat) {
      throw new Error('Chat not found');
    }

    const messageToDelete = chat.messages.find(msg => msg.id === messageId);
    if (!messageToDelete) {
      throw new Error('Message not found');
    }

    // If it's a file message, you might want to delete the file from storage too
    if (messageToDelete.type === 'file' && messageToDelete.fileData) {
      try {
        // File deletion logic would go here (requires storage)
      } catch (fileError) {
        console.warn('Could not delete file from storage:', fileError);
      }
    }

    const updatedMessages = chat.messages.filter(msg => msg.id !== messageId);

    const updatedChat = {
      ...chat,
      messages: updatedMessages,
      updatedAt: new Date().toISOString()
    };

    await writeUserChat(targetChatId, updatedChat);
    return updatedChat;
  } catch (error) {
    console.error('Error deleting message:', error);
    throw error;
  }
};